import type { CanvasContexts } from '@/modules/map-editor';
import type { Node, Edge, NodeSelectionPoint } from '@/modules/map-editor';
import type { MapAreaService } from '@/modules/map-editor';
import type { MapAreaState } from '@/modules/map-editor';

export enum PointerAction {
  IDLE = 'idle',
  HOVER = 'hover',
  DRAG_NODE = 'drag_node',
  RESIZE_NODE = 'resize_node',
  DRAG_MAP = 'drag_map',
  CREATE_NODE = 'create_node',
  CREATE_EDGE = 'create_edge',
  DRAG_SELECTION = 'drag_selection',
  DRAG_MULTI_SELECTION = 'drag_multi_selection'
}

export enum PointerCursor {
  DEFAULT = 'default',
  MOVE = 'move',
  POINTER = 'pointer',
  GRAB = 'grab',
  GRABBING = 'grabbing',
  DRAW_CONNECTION = 'draw-connection',
  DRAW_NODE = 'draw-node',
  LOADING = 'loading',
  RESIZE = 'resize'
}

export interface PointerPosition {
  x: number;
  y: number;
  clientX: number;
  clientY: number;
  movementX: number;
  movementY: number;
}

/** Rectangular bounds for the lasso/drag selection overlay */
export interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export interface PointerState {
  position: PointerPosition;
  action: PointerAction;
  hoveredNodeId: number | null;
  hoveredEdgeId: number | null;
  hoveredSizerPoint: NodeSelectionPoint | null;
  isMouseDown: boolean;
  isShiftPressed: boolean;
  isSpacePressed: boolean;
  isNodeSizing: boolean;
  isHoveringSelectionBox: boolean;
  selectionBox: SelectionBox | null;
}

// ---------------------------------------------------------------------------
// Helper types
// ---------------------------------------------------------------------------

/** Partial node design data used during resize and design save operations */
export interface NodeDesignData {
  width?: number;
  height?: number;
}

/** Tracks the from/to node IDs during two-step edge connection workflow */
export interface EdgeConnectionState {
  from: number | null;
  to: number | null;
}

/** Per-node offset tracked during multi-selection drag */
export interface MultiDragOffset {
  nodeId: number;
  offsetX: number;
  offsetY: number;
}

/** Snapshot of a node's position before a drag operation (for undo support) */
export interface MovedNodeSnapshot {
  id: number;
  x: number;
  y: number;
}

/** Dashboard option descriptor from the legacy DASHBOARD_OPTIONS constant */
export interface DashboardOption {
  VALUE: string;
  TITLE?: string;
  ACTION?: string;
}

/** Pointer state broadcast to other editors during collaborative editing */
export interface CollaborativePointerState {
  editor: { x: number; y: number };
  user: unknown;
}

// ---------------------------------------------------------------------------
// Parameter interfaces
// ---------------------------------------------------------------------------

/**
 * Parameters for handleMove interaction
 */
export interface HandleMoveParams {
  ctx: CanvasRenderingContext2D;
  position: { x: number; y: number };
  zoomLevel: number;
  isShiftPressed?: boolean;
  canvasWidth: number;
  canvasHeight: number;
  nodes: Node[];
  edges: Edge[];
  mapAreaService: MapAreaService;
  sendPointerState: (state: CollaborativePointerState) => void;
  userCurrent: unknown;
  selectedNode: Node | null;
  updateNodeDesign: (designData: NodeDesignData) => void;
  selectedNodes: Node[];
}

/**
 * Parameters for handleDown interaction
 */
export interface HandleDownParams {
  ctx: CanvasRenderingContext2D;
  position: { x: number; y: number };
  zoomLevel: number;
  canvasWidth: number;
  canvasHeight: number;
  nodes: Node[];
  mapAreaService: MapAreaService;
  /** TODO: restore read-only guard when permission system is finalized */
  isReadOnly: boolean;
  hasPermission: boolean;
  selectedNode: Node | null;
  nodeSizerPointHovered: NodeSelectionPoint | null;
  saveNodePosition: (nodeId: number | null) => Promise<void>;
  selectedNodes: Node[];
}

/**
 * Parameters for handleUp interaction
 */
export interface HandleUpParams {
  nodes: Node[];
  edges: Edge[];
  currentLayerId: number;
  mapAreaService: MapAreaService;
  saveNodePosition: (nodeId: number | null | 'save') => Promise<void>;
  saveNodeDesign: (nodeData: NodeDesignData) => Promise<void>;
  setSelectedNode: (nodeId: number | null) => Promise<void>;
  setSelectedEdge: (edgeId: number | null) => Promise<void>;
  toggleNodeOptions: (status: boolean) => Promise<void>;
  toggleEdgeOptions: (status: boolean) => Promise<void>;
  isReadOnly: boolean;
  hasPermission: boolean;
  isQuickEditBarContentTracked: boolean;
  updateQuickEditBarContentWarning: (status: boolean) => Promise<void>;
  isNodeDrawingMode: boolean;
  isEdgeConnectionsDrawingMode: boolean;
  edgeConnections: EdgeConnectionState;
  createNode: (event: MouseEvent | TouchEvent, ctx: CanvasRenderingContext2D, zoomLevel: number, mapArea: MapAreaState) => Promise<Node | null>;
  connectEdge: (nodeId: number) => Promise<void>;
  createEdge: () => Promise<void>;
  markNodeConnect: (nodeId: number) => Promise<void>;
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  event: MouseEvent | TouchEvent;
  ctx: CanvasRenderingContext2D;
  zoomLevel: number;
  mapArea: MapAreaState;
  editedNode: Node | null;
  editedEdge: Edge | null;
  stopEditingNode: () => Promise<void>;
  stopEditingEdge: () => Promise<void>;
  isNodeDesignSettingsOpened: boolean;
  isEdgeDesignSettingsOpened: boolean;
  sectionActive: string | null;
  chooseOption: (option: DashboardOption | null) => Promise<void>;
  toggleSection: (section: string) => Promise<void>;
  closeSidebar: () => Promise<void>;
  cancelEdgeConnection: () => Promise<void>;
  adjustMapForQuickEditBar: (node: Node, ctx: CanvasRenderingContext2D, zoomLevel: number, mapArea: MapAreaState) => Promise<void>;
  updateNodeDesign?: (designData: NodeDesignData) => Promise<void>;
  isMapTransfer: boolean;
  isShiftPressed: boolean;
  selectNode: (node: Node) => Promise<void>;
  deselectAllNodes: () => Promise<void>;
  selectMultipleNodes: (nodes: Node[]) => Promise<void>;
  selectedCount: number;
}

// ---------------------------------------------------------------------------
// Sizer point
// ---------------------------------------------------------------------------

/**
 * Resize handle attached to a selected node's corner.
 * `position` matches SIZER_POINTS constants (e.g. `{ value: 'top-left' }`).
 * `pointer` is a CSS cursor string (e.g. 'nw-resize').
 */
export interface SizerPoint {
  position: { value: string };
  pointer: string;
}

export enum SizerPointPosition {
  TOP_LEFT = 'top_left',
  TOP_RIGHT = 'top_right',
  BOTTOM_LEFT = 'bottom_left',
  BOTTOM_RIGHT = 'bottom_right'
}

export interface PointerEventContext {
  event: MouseEvent | TouchEvent;
  canvas: HTMLCanvasElement;
  contexts: CanvasContexts;
  zoomLevel: number;
  pixelRatio: number;
}

export interface HoverDetectionResult {
  hoveredNodeId: number | null;
  hoveredEdgeId: number | null;
}
